import {
  ClubOnboardingSettingsDTO,
  Iso2Country,
  UserMemberProfileDTOPatch,
} from "@gymflow/types";
import { DateTime } from "luxon";
import { z } from "zod";

import { validatePhoneNumber } from "./lib/helpers/phoneNumber";
import { validatePostCode } from "./lib/helpers/postCode";

const mapSettingsToFields = {
  mobileNumber: "mobileNumberSetting",
  dateBirth: "dateBirthSetting",
  gender: "genderSetting",
  personalNumber: "personalNumberSetting",
  addressLine1: "addressSetting",
  addressLine2: "addressSetting",
  postCode: "postCodeSetting",
  city: "citySetting",
  emergencyContact: "emergencyContactSetting",
  emergencyContactName: "emergencyContactSetting",
  picture: "pictureSetting",
  acceptClubWaiver: "clubWaiverAcceptedSetting",
} satisfies Record<
  keyof UserMemberProfileDTOPatch | "picture",
  keyof ClubOnboardingSettingsDTO | "clubWaiverAcceptedSetting"
>;

const schemaEntries = (country: Iso2Country, mobileNumber: string) =>
  ({
    mobileNumber: z.string().refine((e) => validatePhoneNumber(e), {
      message: "validation.phoneNumber",
    }),
    dateBirth: z.string().refine(
      (e) => {
        return e === undefined || DateTime.fromISO(e) < DateTime.now();
      },
      {
        message: "validation.dateTooBig",
        params: {
          maximum: DateTime.now().toLocaleString(DateTime.DATE_MED),
        },
      },
    ),
    gender: z.enum(["MALE", "FEMALE", "PREFER_NOT_TO_SAY"]),
    personalNumber: z.string().min(1, "common.required"),
    addressLine1: z.string().min(3),
    addressLine2: z.string().optional(),
    postCode: z
      .string()
      .trim()
      .refine((e) => validatePostCode(e, country), {
        message: "validation.postCode",
      }),
    city: z.string().min(3),
    emergencyContactName: z.string().min(3),
    emergencyContact: z
      .string()
      .refine((e) => validatePhoneNumber(e), {
        message: "validation.phoneNumber",
      })
      .refine((e) => e !== mobileNumber, {
        message: "validation.noDuplicateContact",
      }),
    picture: z.string(),
    acceptClubWaiver: z.coerce.boolean().refine((bool) => bool === true, {
      message: "validation.waiverNotAccepted",
    }),
  }) satisfies Record<
    keyof UserMemberProfileDTOPatch | "picture",
    z.ZodTypeAny
  >;

const steps = [
  "personalDetails",
  "addressDetails",
  "emergencyContactDetails",
  "waiverDetails",
] as const;

const formSchemaKeys = {
  personalDetails: [
    "mobileNumber",
    "dateBirth",
    "gender",
    "personalNumber",
    "picture",
  ],
  addressDetails: ["addressLine1", "addressLine2", "postCode", "city"],
  emergencyContactDetails: ["emergencyContactName", "emergencyContact"],
  waiverDetails: ["acceptClubWaiver"],
} satisfies {
  [key in (typeof steps)[number]]: (
    | keyof UserMemberProfileDTOPatch
    | "picture"
  )[];
};

export const OnboardingSchemaHelper = {
  generator: (
    settings: ClubOnboardingSettingsDTO,
    country: Iso2Country,
    mobileNumber: string,
  ) => {
    const innerSettings = {
      ...settings,
      clubWaiverAcceptedSetting: "REQUIRED",
    };
    const perCountrySchemaEntries = schemaEntries(country, mobileNumber);
    return Object.entries(formSchemaKeys)
      .map((params) => {
        const [key, value] = params as [
          string,
          (keyof UserMemberProfileDTOPatch)[],
        ];
        let innerSchema = z.object({});
        value.forEach((key) => {
          const setting = innerSettings[mapSettingsToFields[key]];
          if (setting !== "DISABLED") {
            innerSchema = innerSchema.extend({
              [key]:
                setting === "OPTIONAL"
                  ? perCountrySchemaEntries[key].optional().or(z.literal(""))
                  : perCountrySchemaEntries[key],
            });
          }
        });
        return [key, innerSchema] as const;
      })
      .reduce(
        (acc, curr) => ({ ...acc, [curr[0]]: curr[1] }),
        {} as Record<keyof typeof formSchemaKeys, z.ZodTypeAny>,
      );
  },
  getSteps: (settings: ClubOnboardingSettingsDTO) => {
    const innerSettings = {
      ...settings,
      clubWaiverAcceptedSetting: "REQUIRED",
    };
    return steps.filter(
      (step) =>
        !formSchemaKeys[step].every(
          (k) => innerSettings[mapSettingsToFields[k]] === "DISABLED",
        ),
    );
  },
  formSchemaKeys,
  defaultValues: {
    mobileNumber: undefined,
    dateBirth: undefined,
    gender: undefined,
    personalNumber: undefined,
    addressLine1: undefined,
    addressLine2: undefined,
    postCode: undefined,
    city: undefined,
    emergencyContactName: undefined,
    emergencyContact: undefined,
    picture: undefined,
    clubWaiverAccepted: false,
  },
};
